import type { ListSpacesParams } from '~/shared/space/space-list.params'

// ============================================================================
// Filter Relaxation Configuration & Helpers for Suggested Spaces
// This file is imported by both useSuggestedSpaces.tsx and SpaceList.ts
// to avoid circular dependencies
// ============================================================================

/** Capacity range after relaxation */
export interface RelaxedCapacityRange {
  min: number
  max: number
}

/**
 * Compute relaxed capacity range for suggested spaces.
 * Uses tolerance: Min = 0.75X, Max = 2X
 */
export const computeRelaxedCapacityRange = (value: number): RelaxedCapacityRange => {
  return {
    min: Math.max(1, Math.round(value * 0.75)),
    max: Math.max(1, Math.round(value * 2)),
  }
}

/**
 * "Other filters" that can be progressively relaxed (beyond location, capacity, vibe).
 * Each entry maps to its corresponding ListSpacesParams field.
 */
export const RELAXABLE_FILTER_KEYS = [
  'neighborhoods',
  'cuisines',
  'venue_types',
  'prices',
  'michelin_ratings',
  'privacy',
  'noise',
  'space_types',
  'outdoor_space_types',
  'windows',
  'amenities',
  'av',
  'min_google_rating',
  'f_and_b_minimum_from',
  'f_and_b_minimum_to',
] as const

export type RelaxableFilterKey = (typeof RELAXABLE_FILTER_KEYS)[number]

/**
 * Check if a filter value is "active" (non-empty).
 */
export const isFilterActive = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'number') return true
  if (Array.isArray(value)) return value.length > 0
  return false
}

/**
 * Get active relaxable filters from params (filters beyond location, capacity, vibe).
 */
export const getActiveRelaxableFilters = (params: ListSpacesParams): RelaxableFilterKey[] => {
  return RELAXABLE_FILTER_KEYS.filter((key) => isFilterActive(params[key]))
}
